import { DiscordSDK } from '@discord/embedded-app-sdk';
import { DISCORD_CONFIG } from './config.js';

class DiscordManager {
  constructor() {
    this.sdk = null;
    this.auth = null;
    this.participants = new Map();
    this.onParticipantsUpdate = null;
    this.onPlayerPositionUpdate = null;
    this.instanceId = null;
  }

  async initialize() {
    try {
      // Inicializa o Discord SDK
      this.sdk = new DiscordSDK(DISCORD_CONFIG.clientId);
      
      console.log('Discord SDK inicializado');

      // Aguarda o SDK estar pronto
      await this.sdk.ready();
      console.log('Discord SDK pronto');

      // Autoriza a aplicação
      const { code } = await this.sdk.commands.authorize({
        client_id: DISCORD_CONFIG.clientId,
        response_type: 'code',
        state: '',
        prompt: 'none',
        scope: [
          'identify',
          'guilds'
        ],
      });

      console.log('Código de autorização recebido:', code);

      // Troca o código por token de acesso
      const response = await fetch('/api/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
        }),
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const { access_token } = await response.json();
      console.log('Token de acesso recebido');

      // Autentica com o token
      this.auth = await this.sdk.commands.authenticate({
        access_token,
      });

      console.log('Autenticado como:', this.auth.user.username);

      // Subscreve para eventos de participantes
      await this.subscribeToParticipants();

      return this.auth;
    } catch (error) {
      console.error('Erro ao inicializar Discord:', error);
      console.error('Detalhes do erro:', error.message, error.stack);
      throw error;
    }
  }

  async subscribeToParticipants() {
    if (!this.sdk) return;

    try {
      // Obtém participantes iniciais
      const activity = await this.sdk.commands.getInstanceConnectedParticipants();
      
      this.updateParticipants(activity.participants);

      // Subscreve para atualizações
      this.sdk.subscribe('ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE', (data) => {
        this.updateParticipants(data.participants);
      });

      // Subscreve para mensagens de posição
      this.sdk.subscribe('ACTIVITY_INSTANCE_MESSAGE_UPDATE', (data) => {
        this.handlePositionMessage(data);
      });

      // Salva o instance ID do canal
      const channel = await this.sdk.commands.getChannel();
      this.instanceId = channel.id;
      
      console.log('Inscrito para atualizações de participantes e mensagens');
    } catch (error) {
      console.error('Erro ao subscrever participantes:', error);
    }
  }

  updateParticipants(participants) {
    this.participants.clear();
    
    participants.forEach(participant => {
      this.participants.set(participant.id, {
        id: participant.id,
        username: participant.username,
        avatar: participant.avatar
      });
    });

    console.log('Participantes atualizados:', this.participants.size);

    if (this.onParticipantsUpdate) {
      this.onParticipantsUpdate(Array.from(this.participants.values()));
    }
  }

  // Mock para desenvolvimento local
  createMockAuth() {
    const mockUser = {
      id: 'mock-' + Math.random().toString(36).substr(2, 9),
      username: 'Usuario_' + Math.floor(Math.random() * 1000),
      discriminator: '0001',
      avatar: null
    };

    this.auth = {
      user: mockUser,
      access_token: 'mock-token'
    };

    // Adiciona usuário mock aos participantes
    this.participants.set(mockUser.id, mockUser);

    // Simula outros usuários para teste
    for (let i = 0; i < 2; i++) {
      const id = 'mock-' + Math.random().toString(36).substr(2, 9);
      this.participants.set(id, {
        id,
        username: 'Usuario_' + Math.floor(Math.random() * 1000),
        discriminator: String(i + 2).padStart(4, '0'),
        avatar: null
      });
    }

    if (this.onParticipantsUpdate) {
      this.onParticipantsUpdate(Array.from(this.participants.values()));
    }

    return this.auth;
  }

  getCurrentUser() {
    return this.auth?.user || null;
  }

  getParticipants() {
    return Array.from(this.participants.values());
  }

  async sendPlayerPosition(position) {
    if (!this.sdk || !this.instanceId) return;

    try {
      // Envia a posição atualizada do jogador
      await this.sdk.commands.sendActivityInstanceMessage({
        type: 'PLAYER_POSITION',
        data: {
          playerId: this.auth.user.id,
          position: position
        }
      });
    } catch (error) {
      console.error('Erro ao enviar posição:', error);
    }
  }

  handlePositionMessage(data) {
    if (data.type === 'PLAYER_POSITION' && this.onPlayerPositionUpdate) {
      const { playerId, position } = data.data;
      
      // Não atualiza a posição do próprio jogador
      if (playerId !== this.auth?.user?.id) {
        this.onPlayerPositionUpdate(playerId, position);
      }
    }
  }
}

export const discordManager = new DiscordManager();
